import {Component, computed, input, inject} from '@angular/core';
import {RotaDataService} from '../../../../services/store/rota-data.service';
import {DayOfWeek} from '../../../../classes/enums/DayOfWeek';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * This component displays an unavailability in a day, on a rota daily page.
 */
@Component({
  selector: 'app-rota-day-unavailable',
  templateUrl: './rota-day-unavailable.component.html',
  styleUrls: ['./rota-day-unavailable.component.less'],
  imports: [SHARED_IMPORTS]
})
export class RotaDayUnavailableComponent {
  private rotaDataService = inject(RotaDataService);


  /** Identifiers of an employee and position. */
  employeePositionId = input<EmployeePositionId>();
  /** The day of week, on which an employee is unavailable. */
  dayOfWeek = input<DayOfWeek>();
  /** A list of each hour from store opening until store closing. */
  hours = input<string[]>();
  /** A cell width (in pixels) of a daily rota grid. */
  cellWidth = input<number>();

  rotaAssignment = computed(() => this.rotaDataService.rota()?.rotaAssignments
          .find(assignment => assignment.hasEqualEmployeeAndDay(this.employeePositionId(), this.dayOfWeek())));
  fullDayAbsenceAssignment = computed(() => this.rotaDataService.rota()?.fullDayAbsenceAssignments
          .find(assignment => assignment.hasEqualEmployeeAndDay(this.employeePositionId(), this.dayOfWeek())))
  halfDayAbsenceAssignment = computed(() => this.rotaDataService.rota()?.halfDayAbsenceAssignments
          .find(assignment => assignment.hasEqualEmployeeAndDay(this.employeePositionId(), this.dayOfWeek())))

  /**
   * Checks if an employee is absent or has no assignments today.
   */
  isAway(): boolean {
    const isAbsent = this.fullDayAbsenceAssignment() !== undefined || this.halfDayAbsenceAssignment() !== undefined;
    const employeeIsOnHolidayAllWeek = this.rotaDataService.employeeOnHolidayAllWeek().get(this.employeePositionId().value);
    const hasNoAssignmentsToday = this.rotaAssignment() === undefined;
    return isAbsent || employeeIsOnHolidayAllWeek || hasNoAssignmentsToday;
  }

  /**
   * Checks if an absence should be displayed as holiday.
   */
  showAsHoliday(): boolean {
    const isOnHolidayAllWeek = this.rotaDataService.employeeOnHolidayAllWeek().get(this.employeePositionId().value);
    const isOnHolidayToday = this.fullDayAbsenceAssignment() !== undefined || this.halfDayAbsenceAssignment() !== undefined;
    return isOnHolidayAllWeek || isOnHolidayToday;
  }

  /**
   * Finds the time, when employee's absence starts.
   */
  getAbsenceStartTime(): string {
    const minGridHour = this.hours()[0];
    if (!this.halfDayAbsenceAssignment()) {
      return minGridHour;
    }
    const absenceStart = this.halfDayAbsenceAssignment().startTime;
    return absenceStart < minGridHour ? minGridHour : absenceStart;
  }

  /**
   * Finds the time, when employee's absence ends.
   */
  getAbsenceEndTime() {
    const maxGridHour = this.hours()[this.hours().length - 1];
    if (!this.halfDayAbsenceAssignment()) {
      return maxGridHour;
    }
    const absenceEnd = this.halfDayAbsenceAssignment().endTime;
    return absenceEnd > maxGridHour ? maxGridHour : absenceEnd;
  }
}
